<script>
import Layout from "@/layouts/main.vue";
import PageHeader from "@/components/page-header";
import appConfig from "../../../../app.config";
import Multiselect from '@suadelabs/vue3-multiselect';
import "@suadelabs/vue3-multiselect/dist/vue3-multiselect.css";
import { required, email, helpers, requiredIf } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { defineComponent, computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';

const UsersCreate = defineComponent({
  setup() {
      const state = ref({
        username: '',
        email: '',
        name: '',
        phone_number: '',
        roles: null,
        lgus: null,
        image: '',
        signature: ''
      });

      const handleUserFileUpload = (event) => {
        const preview = document.querySelectorAll(".user-profile-image")[0];
        const file = event.target.files[0];
        const reader = new FileReader();
        
        reader.addEventListener(
            "load",
            function () {
                preview.src = reader.result;
                  state.value.image = file;
            },
            false
        );

        if (file) {
            reader.readAsDataURL(file);
        }
      }

      const handleUserSignatureFileUpload = (event) => {
        const preview = document.querySelectorAll(".user-profile-image")[1];
        const file = event.target.files[0];
        const reader = new FileReader();

        reader.addEventListener(
            "load",
            function () {
                preview.src = reader.result;
                 state.value.signature = file;
            },
            false
        );

        if (file) {
            reader.readAsDataURL(file);
        }
      }

      const rules = computed(() => ({
        name: {
            required: helpers.withMessage("Full Name is required", required),
        },
        phone_number: {
            required: helpers.withMessage("Phone Number is required", required),
        },
        username: {
            requiredIfFunction: helpers.withMessage("Username is required when Email is not provided",
                requiredIf(() => !state.value.email)),
        },
        email: {
            requiredIfFunction: helpers.withMessage("Email is required when Username is not provided",
                requiredIf(() => !state.value.username)),
            email: helpers.withMessage("Please enter valid email", email),
        },
        lgus: {
            required: helpers.withMessage("Please select at least one LGU", required),
        },
        roles: {
            required: helpers.withMessage("Please select at least one role", required),
        },
      }));

      const store = useStore();
      const v$ = useVuelidate(rules, state.value);

      const title = ref("Add New User");
      const items = ref([
          {
          text: "Home",
          href: "/",
        },
         {
          text: "User Management",
          href: "/user-management/users/list",
          active: true
        },
        {
          text: "Add New User",
          active: true,
        }
      ]);

      const authUser = JSON.parse(localStorage.getItem('user'));
      const authUserRoleIds = new Set((authUser?.roles ?? []).map(r => r.id));
      const authUserLguIds = new Set((authUser?.lgus ?? []).map(l => l.id));
      const isSuperAdmin = (authUser?.roles ?? []).some(r => r.slug === 'super-admin');

      const roleOptions = computed(() => {
          const all = store ? store.state.role.dropdownRoles : [];
          if (isSuperAdmin) return all;
          return all.filter(r => authUserRoleIds.has(r.id));
      });
      const lguOptions = computed(() => {
          const all = store ? store.state.lgu.dropdownLGUs : [];
          return all.filter(l => authUserLguIds.has(l.id));
      });
      const submitted = ref(false);
      const notification = computed(() => store ? store.state.notification : null)
      const loading = computed(() => store.state.globals.loading);

      function createUser() {
        submitted.value = true
        v$.value.$touch();

        if (state.value.email === '') {
            delete state.value.email;
        }

        if (state.value.username === '') {
            delete state.value.username;
        }

        if (!state.value.image) {
            delete state.value.image;
        }

        if (!state.value.signature) {
            delete state.value.signature;
        }

        if (v$.value.$invalid) return;

        const payload = { ...state.value };
        if (payload.roles && !Array.isArray(payload.roles)) {
            payload.roles = [payload.roles];
        }
        store.dispatch('user/createUser', payload)
      }

      function getAllRoles() {
          store.dispatch('role/getDropdownRoles')
      }

      function getAllLGUs() {
          store.dispatch('lgu/getDropdownLGUs')
      }

      onMounted(() => {
          getAllRoles();
          getAllLGUs();
      })

      return {
          loading,
          state,
          v$,
          title,
          items,
          roleOptions,
          lguOptions,
          submitted,
          notification,
          createUser,
          getAllRoles,
          getAllLGUs,
          handleUserFileUpload,
          handleUserSignatureFileUpload
      }
  },
  page: {
    title: "User Management - Add New User",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    Multiselect
  }
});

export default UsersCreate;
</script>

<template>
  <Layout>
    <PageHeader back="/user-management/users/list" :title="title" :items="items" />
    <div class="row">
        <div class="col lg-12">
            <b-overlay :show="loading">
                <div class="card">
                    <div class="card-header">
                        <h4 class="card-title mb-0">Add New User</h4>
                    </div>
                    <div class="card-body">
                        <div v-if="notification && notification.messages.length > 0">
                            <b-alert v-for="(message, index) in notification.messages" :key="index"
                                show variant="danger" class="fade show" role="alert">
                                {{ message }}
                            </b-alert>
                        </div>

                        <form @submit.prevent="createUser" class="mx-2">
                            <div class="text-center">
                                <div class="profile-user position-relative d-inline-block mx-auto mb-2">
                                    <img src="@/assets/images/users/user-dummy-img.jpg" class="rounded-circle avatar-lg img-thumbnail user-profile-image" alt="user-signature-image">
                                    <div class="avatar-xs p-0 rounded-circle profile-photo-edit">
                                        <input @change="handleUserFileUpload" id="profile-img-file-input" type="file" class="profile-img-file-input" accept="image/png, image/jpeg">
                                        <label for="profile-img-file-input" class="profile-photo-edit avatar-xs">
                                            <span class="avatar-title rounded-circle bg-light text-body">
                                                <i class="ri-camera-fill"></i>
                                            </span>
                                        </label>
                                    </div>
                                </div>
                                <h5 class="fs-14">Add User Image</h5>
                            </div>
                            <p class="fs-6 text text-decoration-underline">User Information</p>
                            <div class="row">
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="name" class="form-label required">Full Name</label> 
                                        <input 
                                            :class="{ 'is-invalid': submitted && v$.name.$error }"
                                            v-model="state.name" type="text" class="form-control" placeholder="Enter full name..." id="name" />
                                        <div
                                            v-for="item of v$.name.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="phoneInput" class="form-label required">Phone Number</label> 
                                        <input 
                                            :class="{ 'is-invalid': submitted && v$.phone_number.$error }"
                                            v-maska="['+63 (###) ### ####', '+63 (###) #### ####']"
                                            v-model="state.phone_number" type="text" class="form-control" placeholder="Enter phone number..." id="phoneInput">
                                        <div class="text-muted">e.g +63 (912) 345 6789 / +63 (032) 8123-4567</div>
                                        <div
                                            v-for="item of v$.phone_number.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="username" class="form-label">Username</label>
                                        <input
                                            :class="{ 'is-invalid': submitted && v$.username.$error }"
                                            v-model="state.username" type="text" class="form-control" placeholder="Enter username..." id="username">
                                        <div
                                            v-for="item of v$.username.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="email" class="form-label">Email Address</label>
                                        <input 
                                            :class="{ 'is-invalid': submitted && v$.email.$error }"
                                            v-model="state.email" type="email" class="form-control" placeholder="Enter email address..." id="email">
                                        <div
                                            v-for="item of v$.email.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="rolesInput" class="form-label required">Roles</label>
                                        <multiselect
                                            :class="{ 'is-invalid': submitted && v$.roles.$error }"
                                            v-model="state.roles"
                                            :close-on-select="true"
                                            :options="roleOptions"
                                            placeholder="Select Role(s)"
                                            label="name"
                                            track-by="name"
                                        />
                                        <div
                                            v-for="item of v$.roles.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="lguInput" class="form-label required">LGU Assign</label>
                                        <multiselect
                                            :class="{ 'is-invalid': submitted && v$.lgus.$error }"
                                            :multiple="true"
                                            v-model="state.lgus" 
                                            :close-on-select="true"
                                            :options="lguOptions"
                                            placeholder="Select LGU Assign"
                                            label="name"
                                            track-by="name"
                                        />
                                        <div
                                            v-for="item of v$.lgus.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="eSignatureInput" class="form-label d-block">e-Signature</label> 
                                        <div class="profile-user position-relative d-inline-block mx-auto mb-2">
                                            <img src="@/assets/images/users/signature-img.jpeg" class="rounded-circle avatar-lg img-thumbnail user-profile-image" alt="user-profile-image">
                                            <div class="avatar-xs p-0 rounded-circle profile-photo-edit">
                                                <input id="profile-sig-file-input" @change="handleUserSignatureFileUpload" type="file" class="profile-img-file-input" accept="image/png, image/jpeg">
                                                <label for="profile-sig-file-input" class="profile-photo-edit avatar-xs">
                                                    <span class="avatar-title rounded-circle bg-light text-body">
                                                        <i class="ri-camera-fill"></i>
                                                    </span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        <div class="row">
                            <div class="col-lg-12">
                                <div class="text-end">
                                     <router-link to="/user-management/users/list" type="submit" class="btn btn-danger mr-3">
                                        <i class="ri-close-fill fs-4 mr-1 valign-middle"></i>
                                        Cancel
                                     </router-link>
                                    <button type="submit" class="btn btn-success">
                                        <i class="ri-save-2-fill fs-4 mr-1 valign-middle"></i>
                                        Create User
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
                </div>
            </b-overlay>
        </div>
    </div>
  </Layout>
</template>